import React, { useState, useEffect } from "react";
import {
  Container,
  Typography,
  Box,
  Paper,
  Tabs,
  Tab,
  Alert,
  Button,
  CircularProgress,
  TextField,
  Stack,
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import { useDexService } from "../../contexts/DexServiceContext";
import { ethers } from "ethers";

import ServiceStatus from "./components/ServiceStatus";
import TokenInfo from "./components/TokenInfo";
import WalletBalance from "./components/WalletBalance";
import TokenApproval from "./components/TokenApproval";
import SwapTester from "./components/SwapTester";
import LogViewer from "./components/LogViewer";

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

const TabPanel: React.FC<TabPanelProps> = ({
  children,
  value,
  index,
  ...other
}) => {
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`dex-tester-tabpanel-${index}`}
      aria-labelledby={`dex-tester-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ p: 3 }}>{children}</Box>}
    </div>
  );
};

/**
 * DEX测试工具页面
 * 用于测试DexService的功能，包括：
 * - 查询代币信息
 * - 查询钱包余额
 * - 测试代币授权
 * - 执行批量交易测试
 */
const DexTester: React.FC = () => {
  const {
    dexService,
    config,
    setDexServiceConfig,
    isInitialized,
    logs,
    initialize,
  } = useDexService();
  const [tabValue, setTabValue] = useState(0);
  const [tokenAddress, setTokenAddress] = useState("");
  const [error, setError] = useState<string>("");
  const navigate = useNavigate();

  // 监听配置变化并重新初始化
  useEffect(() => {
    const loadConfig = async () => {
      try {
        const savedConfig = localStorage.getItem("dexServiceConfig");
        
        if (savedConfig) {
          const config = JSON.parse(savedConfig);
          if (!isInitialized) {
            await initialize(config);
          } else {
            setDexServiceConfig(config);
          }
        }
      } catch (error) {
        console.error("加载配置失败:", error);
      }
    };
    loadConfig();
  }, [initialize]);

  // 处理标签页切换
  const handleTabChange = (_event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
  };

  // 跳转到配置页面
  const handleGoToConfig = () => {
    navigate("/dex-config");
  };

  // 验证代币地址
  const validateTokenAddress = (address: string) => {
    if (!address) {
      setError("请输入代币地址");
      return false;
    }

    try {
      // 检查地址格式
      if (!ethers.utils.isAddress(address)) {
        setError("无效的代币地址格式");
        return false;
      }

      // 如果配置了Four.meme路由器，检查代币是否支持
      const supportedTokens = config?.dex.router.name === "Four.meme" ? config?.dex.router.address : '';
      if (
        supportedTokens &&
        supportedTokens.length > 0 &&
        !supportedTokens.includes(address.toLowerCase())
      ) {
        setError("该代币不支持 Four.meme 路由器");
        return false;
      }

      setError("");
      return true;
    } catch (err) {
      setError("代币地址验证失败");
      return false;
    }
  };

  // 处理代币地址输入
  const handleTokenAddressChange = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const address = event.target.value;
    setTokenAddress(address);
    if (address) {
      validateTokenAddress(address);
    } else {
      setError("");
    }
  };

  // 如果DexService未初始化，显示提示
  if (!isInitialized) {
    return (
      <Container maxWidth="lg">
        <Box sx={{ my: 4 }}>
          <Typography variant="h4" component="h1" gutterBottom>
            DEX 测试工具
          </Typography>

          <Alert severity="warning" sx={{ mb: 2 }}>
            DexService尚未初始化。请先配置DexService。
          </Alert>

          <Button
            variant="contained"
            color="primary"
            onClick={handleGoToConfig}
          >
            前往配置页面
          </Button>
        </Box>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg">
      <Box sx={{ my: 4 }}>
        <Typography variant="h4" component="h1" gutterBottom>
          DEX 测试工具
        </Typography>
        <Typography variant="body1" paragraph>
          使用此工具测试DEX服务的各项功能，包括查询代币信息、余额查询、代币授权和批量交易测试。
        </Typography>

        <ServiceStatus />

        <Stack spacing={2} sx={{ mt: 3 }}>
          <Paper sx={{ mt: 3, p: 3 }}>
            <TextField
              label="代币地址"
              fullWidth
              value={tokenAddress}
              onChange={handleTokenAddressChange}
              error={!!error}
              helperText={error}
              placeholder="输入要测试的代币地址"
              InputProps={{
                sx: { fontFamily: "monospace" },
              }}
            />
          </Paper>
        </Stack>

        <Paper sx={{ mt: 3 }}>
          <Box sx={{ borderBottom: 1, borderColor: "divider" }}>
            <Tabs
              value={tabValue}
              onChange={handleTabChange}
              aria-label="DEX测试选项卡"
            >
              <Tab
                label="代币信息"
                id="dex-tester-tab-0"
                aria-controls="dex-tester-tabpanel-0"
              />
              <Tab
                label="钱包余额"
                id="dex-tester-tab-1"
                aria-controls="dex-tester-tabpanel-1"
              />
              <Tab
                label="代币授权"
                id="dex-tester-tab-2"
                aria-controls="dex-tester-tabpanel-2"
              />
              <Tab
                label="批量交易"
                id="dex-tester-tab-3"
                aria-controls="dex-tester-tabpanel-3"
              />
              <Tab
                label="日志查看"
                id="dex-tester-tab-4"
                aria-controls="dex-tester-tabpanel-4"
              />
            </Tabs>
          </Box>

          <TabPanel value={tabValue} index={0}>
            <TokenInfo tokenAddress={tokenAddress} />
          </TabPanel>

          <TabPanel value={tabValue} index={1}>
            <WalletBalance tokenAddress={tokenAddress} />
          </TabPanel>

          <TabPanel value={tabValue} index={2}>
            <TokenApproval tokenAddress={tokenAddress} />
          </TabPanel>

          <TabPanel value={tabValue} index={3}>
            <SwapTester tokenAddress={tokenAddress} />
          </TabPanel>

          <TabPanel value={tabValue} index={4}>
            <LogViewer />
          </TabPanel>
        </Paper>
      </Box>
    </Container>
  );
};

export default DexTester;
